"use client";
import { jsxs, jsx } from "react/jsx-runtime";
import React, { Suspense } from "react";
import { FormControl, FormLabel, RadioGroup } from "@mui/material";
import { visuallyHidden } from "@mui/utils";
import { Info } from "@nxt-costco-com/forge-icons";
import { Text } from "../../../DataDisplay/Text/index.js";
import { SelectorRadioButton } from "../../RadioButton/SelectorRadioButton/index.js";
import { INVALID_PROPS_ERROR, MISSING_LEGEND_WARNING, MISSING_ARIA_LABEL_PREFIX_WARNING, COMPONENT_NAME } from "./constants.js";
import { SelectorRadioButtonGroupStyles } from "./styles.js";
import { getDataTestId } from "../../../Util/index.js";
const Notification = React.lazy(
  () => import("../../../Feedback/Notification/index.js").then((module) => ({
    default: module.Notification
  }))
);
const SelectorRadioButtonGroup = (props) => {
  const {
    accessibleLegendText,
    accessibleLegendId,
    accessibleLegendTextProps,
    isAccessibleLegendVisible = false,
    ariaLabelledBy,
    options,
    onChange,
    selectedOption,
    errorText,
    ariaLabelErrorPrefix,
    uniqueId,
    required
  } = props;
  const hasError = !!errorText;
  if (!options || options.length === 0) {
    console.error(INVALID_PROPS_ERROR);
    return null;
  }
  if (!accessibleLegendText && !ariaLabelledBy) {
    console.warn(MISSING_LEGEND_WARNING);
  }
  if (hasError && !ariaLabelErrorPrefix) {
    console.warn(MISSING_ARIA_LABEL_PREFIX_WARNING);
  }
  const styles = SelectorRadioButtonGroupStyles();
  const componentUniqueId = getDataTestId(COMPONENT_NAME, uniqueId);
  const errorId = `${componentUniqueId}-error`;
  const errorIconA11yAttributes = {
    "aria-label": `${ariaLabelErrorPrefix}: `,
    "aria-hidden": false,
    focusable: true
  };
  return /* @__PURE__ */ jsxs(
    FormControl,
    {
      "data-testid": componentUniqueId,
      component: "fieldset",
      "aria-invalid": hasError,
      "aria-describedby": hasError ? errorId : void 0,
      "aria-labelledby": ariaLabelledBy,
      role: required && "radiogroup",
      "aria-required": required,
      children: [
        accessibleLegendText && /* @__PURE__ */ jsx(
          FormLabel,
          {
            id: accessibleLegendId,
            component: "legend",
            sx: isAccessibleLegendVisible ? {} : visuallyHidden,
            children: /* @__PURE__ */ jsx(Text, { ...accessibleLegendTextProps, children: accessibleLegendText })
          }
        ),
        /* @__PURE__ */ jsxs(
          RadioGroup,
          {
            role: void 0,
            value: selectedOption,
            onChange: (event) => onChange(event.target.value),
            sx: styles.radioGroup,
            children: [
              hasError && /* @__PURE__ */ jsx("div", { id: errorId, children: /* @__PURE__ */ jsx(Suspense, { fallback: null, children: /* @__PURE__ */ jsx(
                Notification,
                {
                  icon: /* @__PURE__ */ jsx(Info, { ...errorIconA11yAttributes }),
                  message: errorText,
                  uniqueId,
                  severity: "critical"
                }
              ) }) }),
              options.map((option, i) => /* @__PURE__ */ jsx(
                SelectorRadioButton,
                {
                  ariaLabelErrorPrefix,
                  checked: option.value === selectedOption ? true : void 0,
                  uniqueId: `${uniqueId}-${i}`,
                  ...option
                },
                option.value
              ))
            ]
          }
        )
      ]
    }
  );
};
SelectorRadioButtonGroup.displayName = COMPONENT_NAME;
export {
  SelectorRadioButtonGroup
};
